/**
 * TabBar Component for Poker Reborn
 *
 * Reusable horizontal tab bar for switching between content panels.
 * Used in Settings screen for Sound/Graphics/Play tabs.
 *
 * @see docs/screens/settings/README.md - TabBar specs and styling
 */

import { Container, Graphics, Text } from 'pixi.js';

// =============================================================================
// TYPES
// =============================================================================

export interface TabDefinition {
  /** Unique identifier for the tab */
  id: string;
  /** Icon displayed before label (emoji or text) */
  icon?: string;
  /** Tab label text */
  label: string;
}

export interface TabBarOptions {
  /** Tab definitions */
  tabs: TabDefinition[];
  /** Total width of the tab bar */
  width: number;
  /** Height of the tab bar */
  height?: number;
  /** Initially active tab id */
  activeTab?: string;
}

export interface TabBarStyle {
  /** Background color of inactive tab */
  inactiveBackground: number;
  /** Text color of inactive tab */
  inactiveText: number;
  /** Background color on hover */
  hoverBackground: number;
  /** Text color on hover */
  hoverText: number;
  /** Background color of active tab */
  activeBackground: number;
  /** Text color of active tab */
  activeText: number;
  /** Active indicator color (bottom border) */
  activeIndicator: number;
  /** Font size */
  fontSize: number;
}

// =============================================================================
// DEFAULT STYLES
// =============================================================================

const DEFAULT_STYLE: TabBarStyle = {
  inactiveBackground: 0x00000000, // transparent
  inactiveText: 0x888888,
  hoverBackground: 0x333344,
  hoverText: 0xaaaaaa,
  activeBackground: 0x4a8a44,
  activeText: 0xffffff,
  activeIndicator: 0x6aba64,
  fontSize: 16,
};

const TAB_HEIGHT = 48;
const TAB_GAP = 8;
const TAB_PADDING = 16;
const INDICATOR_HEIGHT = 3;
const BORDER_RADIUS = 8;

// =============================================================================
// TAB BAR COMPONENT
// =============================================================================

export class TabBar extends Container {
  private tabs: TabDefinition[];
  private tabWidth: number;
  private barHeight: number;
  private style: TabBarStyle;

  private activeTabId: string;
  private tabContainers: Map<string, Container> = new Map();
  private tabBackgrounds: Map<string, Graphics> = new Map();
  private tabTexts: Map<string, Text> = new Map();

  /** Callback when tab is selected */
  public onTabChange?: (tabId: string) => void;

  constructor(options: TabBarOptions) {
    super();

    this.tabs = options.tabs;
    this.barHeight = options.height ?? TAB_HEIGHT;
    this.style = { ...DEFAULT_STYLE };

    // Calculate tab width (equal distribution with gaps)
    const totalGaps = (this.tabs.length - 1) * TAB_GAP;
    this.tabWidth = (options.width - totalGaps) / this.tabs.length;

    // Set initial active tab
    this.activeTabId = options.activeTab ?? (this.tabs[0]?.id ?? '');

    // Create tabs
    this.createTabs(options.width);
  }

  // ===========================================================================
  // PUBLIC API
  // ===========================================================================

  /** Get currently active tab id */
  getActiveTab(): string {
    return this.activeTabId;
  }

  /** Set active tab by id */
  setActiveTab(tabId: string): void {
    if (tabId === this.activeTabId) return;
    if (!this.tabs.some((tab) => tab.id === tabId)) return;

    const previousTabId = this.activeTabId;
    this.activeTabId = tabId;

    // Update visual states
    this.updateTabState(previousTabId, 'inactive');
    this.updateTabState(tabId, 'active');
  }

  /** Update tab bar width */
  setWidth(width: number): void {
    const totalGaps = (this.tabs.length - 1) * TAB_GAP;
    this.tabWidth = (width - totalGaps) / this.tabs.length;

    // Reposition tabs
    this.tabs.forEach((tab, index) => {
      const tabContainer = this.tabContainers.get(tab.id);
      if (tabContainer) {
        tabContainer.x = index * (this.tabWidth + TAB_GAP);
        this.redrawTabBackground(tab.id);
      }
    });
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private createTabs(_totalWidth: number): void {
    this.tabs.forEach((tab, index) => {
      const tabContainer = new Container();
      tabContainer.x = index * (this.tabWidth + TAB_GAP);
      tabContainer.eventMode = 'static';
      tabContainer.cursor = 'pointer';

      // Tab background
      const background = new Graphics();
      tabContainer.addChild(background);
      this.tabBackgrounds.set(tab.id, background);

      // Tab text (icon + label)
      const displayText = tab.icon ? `${tab.icon} ${tab.label}` : tab.label;
      const text = new Text({
        text: displayText,
        style: {
          fill: this.style.inactiveText,
          fontSize: this.style.fontSize,
          fontFamily: 'Roboto, Arial, sans-serif',
          fontWeight: 'bold',
        },
      });
      text.anchor.set(0.5);
      text.x = this.tabWidth / 2;
      text.y = this.barHeight / 2;
      tabContainer.addChild(text);
      this.tabTexts.set(tab.id, text);

      // Draw initial background
      this.drawTabBackground(
        background,
        tab.id === this.activeTabId ? 'active' : 'inactive'
      );

      // Update text color for active tab
      if (tab.id === this.activeTabId) {
        text.style.fill = this.style.activeText;
      }

      // Event handlers
      tabContainer.on('pointerover', () => {
        if (tab.id !== this.activeTabId) {
          this.updateTabState(tab.id, 'hover');
        }
      });

      tabContainer.on('pointerout', () => {
        if (tab.id !== this.activeTabId) {
          this.updateTabState(tab.id, 'inactive');
        }
      });

      tabContainer.on('pointertap', () => {
        if (tab.id !== this.activeTabId) {
          this.setActiveTab(tab.id);
          this.onTabChange?.(tab.id);
        }
      });

      this.addChild(tabContainer);
      this.tabContainers.set(tab.id, tabContainer);
    });
  }

  private drawTabBackground(
    graphics: Graphics,
    state: 'inactive' | 'hover' | 'active'
  ): void {
    graphics.clear();

    let bgColor: number;
    let bgAlpha = 1;

    switch (state) {
      case 'active':
        bgColor = this.style.activeBackground;
        break;
      case 'hover':
        bgColor = this.style.hoverBackground;
        break;
      default:
        bgColor = this.style.inactiveBackground;
        bgAlpha = 0;
    }

    // Main background
    graphics.roundRect(0, 0, this.tabWidth, this.barHeight, BORDER_RADIUS);
    graphics.fill({ color: bgColor, alpha: bgAlpha });

    // Active indicator (bottom border)
    if (state === 'active') {
      graphics.roundRect(
        TAB_PADDING,
        this.barHeight - INDICATOR_HEIGHT,
        this.tabWidth - TAB_PADDING * 2,
        INDICATOR_HEIGHT,
        INDICATOR_HEIGHT / 2
      );
      graphics.fill(this.style.activeIndicator);
    }
  }

  private updateTabState(
    tabId: string,
    state: 'inactive' | 'hover' | 'active'
  ): void {
    const background = this.tabBackgrounds.get(tabId);
    const text = this.tabTexts.get(tabId);

    if (!background || !text) return;

    // Update background
    this.drawTabBackground(background, state);

    // Update text color
    switch (state) {
      case 'active':
        text.style.fill = this.style.activeText;
        break;
      case 'hover':
        text.style.fill = this.style.hoverText;
        break;
      default:
        text.style.fill = this.style.inactiveText;
    }
  }

  private redrawTabBackground(tabId: string): void {
    const background = this.tabBackgrounds.get(tabId);
    if (!background) return;

    const state = tabId === this.activeTabId ? 'active' : 'inactive';
    this.drawTabBackground(background, state);

    // Update text position
    const text = this.tabTexts.get(tabId);
    if (text) {
      text.x = this.tabWidth / 2;
    }
  }
}
